// Cute mode label shown under the search bar.
//
// Effect stack (all running concurrently and composing):
//   - Each letter rotates back and forth ("wobble") with a per-letter delay
//   - Each letter cycles through a pastel palette on a 5s loop, staggered
//   - 28 tiny sparkle dots emit from across the word and drift outward
//   - The entire badge sways left/right with a gentle rotation on a 7s loop
//     as if caught in a breeze

export interface ModeBubbleProps {
  label: string;
}

const BASE_CLASSES = 'font-diary font-medium text-white text-2xl tracking-wide';

const STYLE_ID = 'mode-badge-keyframes';
const KEYFRAME_CSS = `
@keyframes mb-particle-drift {
  0%   { transform: translate(0, 0) scale(0); opacity: 0; }
  20%  { opacity: 1; }
  80%  { opacity: 1; }
  100% { transform: translate(var(--mb-dx, 14px), var(--mb-dy, -16px)) scale(1); opacity: 0; }
}
@keyframes mb-wobble {
  0%, 100% { transform: rotate(0deg) translateY(0); }
  20%      { transform: rotate(-6deg) translateY(-2px); }
  50%      { transform: rotate(0deg) translateY(0); }
  80%      { transform: rotate(6deg) translateY(-2px); }
}
@keyframes mb-hue-cycle {
  0%   { color: #ffd1dc; }   /* pink */
  20%  { color: #d4b8f0; }   /* lavender */
  40%  { color: #fff0c8; }   /* cream */
  60%  { color: #b6e8d0; }   /* mint */
  80%  { color: #c0d8ff; }   /* sky */
  100% { color: #ffd1dc; }
}
@keyframes mb-breeze {
  0%, 100% { transform: translate(0, 0) rotate(0deg); }
  25%      { transform: translate(-8px, -3px) rotate(-3deg); }
  50%      { transform: translate(0, 1px) rotate(0deg); }
  75%      { transform: translate(8px, -3px) rotate(3deg); }
}
`;

const ensureKeyframesInjected = (): void => {
  if (typeof document === 'undefined') return;
  if (document.getElementById(STYLE_ID)) return;
  const el = document.createElement('style');
  el.id = STYLE_ID;
  el.textContent = KEYFRAME_CSS;
  document.head.appendChild(el);
};

// Sparkle dots — start from anywhere across the word and drift outward in a
// random radial direction. Deterministic pseudo-random so the layout doesn't
// shuffle on every render.
const PARTICLE_COUNT = 28;

const rand = (i: number): number => {
  const x = Math.sin(i * 12.9898 + 78.233) * 43758.5453;
  return x - Math.floor(x);
};

interface Particle {
  left: string;
  top: string;
  dx: string;
  dy: string;
  delay: string;
  dur: string;
}

const PARTICLES: Array<Particle> = Array.from({ length: PARTICLE_COUNT }, (_, i) => {
  const startX = 5 + rand(i * 3) * 90; // 5%–95% (full word width)
  const startY = 12 + rand(i * 3 + 1) * 76; // 12%–88%
  const angle = rand(i * 3 + 2) * Math.PI * 2;
  const distance = 22 + rand(i * 5) * 32; // 22–54px outward
  const dx = Math.cos(angle) * distance;
  const dy = Math.sin(angle) * distance;
  const delay = (i / PARTICLE_COUNT) * 3.2;
  const dur = 2.4 + rand(i * 7) * 1.2;
  return {
    left: `${startX.toFixed(1)}%`,
    top: `${startY.toFixed(1)}%`,
    dx: `${dx.toFixed(1)}px`,
    dy: `${dy.toFixed(1)}px`,
    delay: `${delay.toFixed(2)}s`,
    dur: `${dur.toFixed(2)}s`,
  };
});

const Particles = () => (
  <>
    {PARTICLES.map((p) => (
      <span
        key={`${p.left}-${p.top}-${p.delay}`}
        aria-hidden
        className="absolute pointer-events-none w-[2px] h-[2px] rounded-full bg-white"
        style={
          {
            left: p.left,
            top: p.top,
            '--mb-dx': p.dx,
            '--mb-dy': p.dy,
            animation: `mb-particle-drift ${p.dur} ease-out ${p.delay} infinite`,
            boxShadow: '0 0 4px rgba(255,255,255,1), 0 0 8px rgba(255,200,240,0.8)',
          } as React.CSSProperties & Record<`--${string}`, string>
        }
      />
    ))}
  </>
);

const BREEZE_STYLE: React.CSSProperties = {
  animation: 'mb-breeze 7s ease-in-out infinite',
};

export const ModeBubble = ({ label }: ModeBubbleProps) => {
  ensureKeyframesInjected();
  return (
    <span
      className={`${BASE_CLASSES} relative inline-block px-4 py-1 rounded-full backdrop-blur-sm shadow-lg`}
      style={{
        ...BREEZE_STYLE,
        // Soft kawaii-gradient pill — keeps the badge readable on light map
        // backgrounds without losing the candy aesthetic.
        background:
          'linear-gradient(135deg, rgba(232,123,168,0.78) 0%, rgba(155,116,232,0.78) 60%, rgba(96,168,224,0.78) 100%)',
        boxShadow: '0 4px 12px rgba(80,30,120,0.25), 0 0 0 1px rgba(255,255,255,0.25) inset',
      }}
    >
      {/* Keyed by `label` so a mode change remounts + replays the morph
          animation: a small slide+fade as the new word arrives. The
          per-character wobble/hue then takes over for the steady state. */}
      <span key={label} className="inline-flex motion-safe:animate-kawaii-mode-morph">
        {[...label].map((ch, i) => (
          <span
            // biome-ignore lint/suspicious/noArrayIndexKey: characters repeat, position is the only stable identifier
            key={i}
            className="inline-block"
            style={{
              animation: `mb-wobble 1.6s ease-in-out ${i * 0.08}s infinite, mb-hue-cycle 5s linear ${-i * 0.4}s infinite`,
              textShadow: '0 1px 0 rgba(80,30,120,0.45), 0 0 10px rgba(255,255,255,0.6)',
              whiteSpace: 'pre',
            }}
          >
            {ch}
          </span>
        ))}
      </span>
      <Particles />
    </span>
  );
};
